/**
 * Pager.jsx - Reusable pagination control.
 *
 * Shows page numbers, prev/next buttons, and total count.
 *
 * Props:
 *   page       {number}   - Current page (1-based).
 *   totalPages {number}   - Total number of pages.
 *   totalRows  {number}   - Total number of rows (for display).
 *   pageSize   {number}   - Current page size.
 *   onPage     {function} - Called with new page number.
 *   onPageSize {function} - Called with new page size.
 *   pageSizes  {number[]} - Available page size options.
 */

import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

const DEFAULT_PAGE_SIZES = [10, 20, 50, 100];

export default function Pager({
  page,
  totalPages,
  totalRows,
  pageSize,
  onPage,
  onPageSize,
  pageSizes = DEFAULT_PAGE_SIZES,
}) {
  if (totalPages <= 1) {
    return (
      <div style={styles.bar}>
        <span style={styles.info}>
          {totalRows} record{totalRows !== 1 ? "s" : ""}
        </span>
      </div>
    );
  }

  /* Build page number list with ellipsis */
  function getPageNumbers() {
    const pages = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);

      let start = Math.max(2, page - 1);
      let end = Math.min(totalPages - 1, page + 1);

      if (page <= 3) {
        start = 2;
        end = 5;
      } else if (page >= totalPages - 2) {
        start = totalPages - 4;
        end = totalPages - 1;
      }

      if (start > 2) pages.push("...");
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalPages - 1) pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  }

  return (
    <div style={styles.bar}>
      {/* Left: row count + page size */}
      <div style={styles.left}>
        <span style={styles.info}>
          {totalRows} record{totalRows !== 1 ? "s" : ""}
        </span>
        <select
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
          style={styles.sizeSelect}
          aria-label="Rows per page"
        >
          {pageSizes.map((s) => (
            <option key={s} value={s}>
              {s} / page
            </option>
          ))}
        </select>
      </div>

      {/* Right: page navigation */}
      <div style={styles.right}>
        <button
          className="btn ghost tiny"
          onClick={() => onPage(1)}
          disabled={page === 1}
          aria-label="First page"
          title="First page"
        >
          <ChevronsLeft size={14} />
        </button>
        <button
          className="btn ghost tiny"
          onClick={() => onPage(page - 1)}
          disabled={page === 1}
          aria-label="Previous page"
          title="Previous page"
        >
          <ChevronLeft size={14} />
        </button>

        {getPageNumbers().map((p, i) =>
          p === "..." ? (
            <span key={`e${i}`} style={styles.ellipsis}>
              ...
            </span>
          ) : (
            <button
              key={p}
              className="btn tiny"
              onClick={() => onPage(p)}
              style={{
                ...(p === page ? styles.activePage : {}),
              }}
            >
              {p}
            </button>
          )
        )}

        <button
          className="btn ghost tiny"
          onClick={() => onPage(page + 1)}
          disabled={page === totalPages}
          aria-label="Next page"
          title="Next page"
        >
          <ChevronRight size={14} />
        </button>
        <button
          className="btn ghost tiny"
          onClick={() => onPage(totalPages)}
          disabled={page === totalPages}
          aria-label="Last page"
          title="Last page"
        >
          <ChevronsRight size={14} />
        </button>
      </div>
    </div>
  );
}

const styles = {
  bar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 16px",
    borderTop: "1px solid var(--line-2)",
    flexWrap: "wrap",
    gap: 8,
  },
  left: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  right: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  info: {
    fontSize: 12,
    color: "var(--ink-3)",
    fontWeight: 500,
  },
  sizeSelect: {
    font: "inherit",
    fontSize: 12,
    padding: "4px 8px",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-sm)",
    background: "var(--panel)",
    color: "var(--ink)",
    cursor: "pointer",
  },
  ellipsis: {
    fontSize: 12,
    color: "var(--ink-3)",
    padding: "0 4px",
  },
  activePage: {
    background: "var(--accent)",
    color: "#fff",
    borderColor: "var(--accent)",
    fontWeight: 650,
  },
};
